import { useId } from 'react'
import styles from './flip-card.module.css'

function star(x: number, y: number, s: number): string {
  const t = s * 0.22
  return `M${x} ${y - s}L${x + t} ${y - t}L${x + s} ${y}L${x + t} ${y + t}L${x} ${y + s}L${x - t} ${y + t}L${x - s} ${y}L${x - t} ${y - t}Z`
}

function Moon({ cx, cy, r, sparks = true, opacity }: { cx: number; cy: number; r: number; sparks?: boolean; opacity?: number }) {
  const id = useId()
  return (
    <>
      <mask id={id}>
        <rect width="180" height="314" fill="#fff" />
        <circle cx={cx + r * 0.42} cy={cy - r * 0.22} r={r * 0.86} fill="#000" />
      </mask>
      <circle cx={cx} cy={cy} r={r} fill="currentColor" mask={`url(#${id})`} opacity={opacity} />
      {sparks && <path fill="currentColor" d={star(cx + r * 0.62, cy + r * 0.5, r * 0.24)} />}
      {sparks && <path fill="currentColor" d={star(cx + r * 1.02, cy + r * 0.02, r * 0.14)} />}
    </>
  )
}

function Corners({ s }: { s: number }) {
  return (
    <>
      {[[22, 24], [158, 24], [22, 290], [158, 290]].map(([x = 0, y = 0]) => (
        <path key={`${x}-${y}`} fill="currentColor" opacity={0.7} d={star(x, y, s)} />
      ))}
    </>
  )
}

const LETTERS = { fontFamily: 'Marcellus, Georgia, serif', fill: 'currentColor', textAnchor: 'middle' } as const

const CONST_P = [[50, 70], [92, 52], [128, 88], [112, 142], [66, 128], [78, 196], [124, 214], [96, 262]] as const
const CONST_L = [[0, 1], [1, 2], [2, 3], [3, 4], [4, 0], [4, 5], [5, 6], [6, 7], [3, 6]] as const
const DUST = Array.from({ length: 26 }, (_, i) => [((Math.sin(i * 12.9898) * 43758.5453) % 1 + 1) % 1, ((Math.sin(i * 78.233) * 24634.6345) % 1 + 1) % 1] as const)

function Art({ variant }: { variant: number }) {
  switch (variant) {
    case 0:
      return (
        <>
          <rect x="20" y="20" width="140" height="274" rx="4" fill="none" stroke="currentColor" strokeOpacity={0.35} />
          <Corners s={5} />
          <Moon cx={84} cy={150} r={34} />
          <text x="90" y="262" fontSize="11" letterSpacing="4" {...LETTERS}>LUNARY</text>
        </>
      )
    case 1:
      return (
        <>
          <path d="M30 300V110a60 60 0 0 1 120 0v190" fill="none" stroke="currentColor" strokeOpacity={0.6} />
          <path d="M40 300V112a50 50 0 0 1 100 0v188" fill="none" stroke="currentColor" strokeOpacity={0.3} />
          <Moon cx={86} cy={140} r={26} />
        </>
      )
    case 2:
      return (
        <>
          <g stroke="currentColor" strokeOpacity={0.75}>
            {CONST_L.map(([a, b]) => (
              <line key={`${a}-${b}`} className={styles.cl} pathLength={100} x1={CONST_P[a][0]} y1={CONST_P[a][1]} x2={CONST_P[b][0]} y2={CONST_P[b][1]} />
            ))}
          </g>
          {CONST_P.map(([x, y], i) => (
            <path key={`${x}-${y}`} className={styles.st} fill="currentColor" d={star(x, y, i % 3 ? 4 : 6)} />
          ))}
          {DUST.map(([x, y], i) => (
            <circle key={i} cx={x * 160 + 10} cy={y * 294 + 10} r={0.8} fill="currentColor" opacity={0.5} />
          ))}
        </>
      )
    case 3:
      return (
        <>
          <g className={styles.sun}>
            {Array.from({ length: 24 }, (_, i) => {
              const a = (i * 15 * Math.PI) / 180
              const r2 = i % 2 ? 64 : 80
              return <line key={i} x1={90 + Math.cos(a) * 36} y1={157 + Math.sin(a) * 36} x2={90 + Math.cos(a) * r2} y2={157 + Math.sin(a) * r2} stroke="currentColor" strokeOpacity={0.7} />
            })}
          </g>
          <circle cx="90" cy="157" r="28" fill="none" stroke="currentColor" />
          <Moon cx={86} cy={157} r={18} sparks={false} />
          <Corners s={4} />
        </>
      )
    case 4:
      return (
        <>
          <circle cx="90" cy="157" r="16" fill="currentColor" opacity={0.85} />
          {[34, 54, 74].map((r, i) => (
            <g key={r} className={`${styles.orb} ${styles[`o${i + 1}`] ?? ''}`}>
              <circle cx="90" cy="157" r={r} fill="none" stroke="currentColor" strokeOpacity={0.45} />
              <circle cx={90 + r} cy="157" r={4 - i} fill="currentColor" />
            </g>
          ))}
          <text x="90" y="40" fontSize="10" letterSpacing="3" opacity={0.8} {...LETTERS}>IV · I · XXI</text>
        </>
      )
    case 5:
      return (
        <>
          <path d="M28 290V104a62 62 0 0 1 124 0v186z" fill="currentColor" fillOpacity={0.08} stroke="currentColor" />
          <path d="M38 280V106a52 52 0 0 1 104 0v174z" fill="none" stroke="currentColor" strokeOpacity={0.4} />
          <Moon cx={86} cy={128} r={24} />
          <text x="90" y="232" fontSize="13" letterSpacing="5" {...LETTERS}>IV</text>
        </>
      )
    case 6:
      return (
        <>
          <rect x="8" y="8" width="164" height="298" rx="7" fill="currentColor" opacity={0.12} />
          <circle cx="90" cy="214" r="46" fill="currentColor" opacity={0.9} />
          <rect x="8" y="214" width="164" height="92" fill="var(--k2)" />
          {[0, 1, 2, 3].map((i) => (
            <line key={i} x1={30 + i * 6} y1={228 + i * 14} x2={150 - i * 6} y2={228 + i * 14} stroke="currentColor" strokeOpacity={0.6 - i * 0.12} />
          ))}
          <Moon cx={84} cy={82} r={20} />
        </>
      )
    case 7:
      return (
        <>
          <rect x="22" y="22" width="136" height="270" rx="4" fill="none" stroke="currentColor" strokeOpacity={0.5} />
          <Moon cx={84} cy={150} r={32} />
          <Corners s={4} />
        </>
      )
    case 8:
      return <Moon cx={84} cy={150} r={30} opacity={0.85} />
    default:
      return (
        <>
          <path d="M90 30l12 26 28 4-20 20 5 28-25-14-25 14 5-28-20-20 28-4z" fill="none" stroke="currentColor" strokeOpacity={0.7} transform="translate(0 90)" />
          <Moon cx={84} cy={156} r={20} sparks={false} />
          <Corners s={4} />
          <path className={styles.crack} d="M90 40l-8 40 14 22-12 30 10 26-6 40 8 30" fill="none" stroke="#ffe7a8" strokeWidth="2" />
          <path className={`${styles.crack} ${styles.c2}`} d="M30 150l30 6 16-12 20 8" fill="none" stroke="#ffe7a8" strokeWidth="1.6" />
          <path className={`${styles.crack} ${styles.c3}`} d="M150 210l-26-4-14 14-16-6" fill="none" stroke="#ffe7a8" strokeWidth="1.6" />
        </>
      )
  }
}

export function FlipBack({ variant }: { variant: number }) {
  return (
    <>
      {variant === 7 && <div className={styles.foil} />}
      {variant === 8 && (
        <>
          <div className={`${styles.blob} ${styles.b1}`} />
          <div className={`${styles.blob} ${styles.b2}`} />
          <div className={`${styles.blob} ${styles.b3}`} />
        </>
      )}
      <svg className={styles.art} viewBox="0 0 180 314" aria-hidden="true">
        <Art variant={variant} />
      </svg>
    </>
  )
}
